import { Col, Row } from 'antd'
import {
  EditorContent,
  MainHeading,
  SecondGreyText,
  StyledP,
  SubFLex,
} from 'modules/Resume/Style/style'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { COLORS } from 'theme'
import { ExclamationCircleFilled } from '@ant-design/icons'
import styled from 'styled-components'

const PendingIcon = styled(ExclamationCircleFilled)`
  color: #eb5d60;
  font-size: 16px;
  margin-left: 8px;
  position: relative;
  top: -5px;
`

const AdditionalInfo = ({ studentDetails, pendingData }) => {
  const splitArray = list => {
    return list?.map(item => item?.trim()).join(', ')
  }
  const hasValidLocation = list => {
    if (!Array.isArray(list)) return false
    return list.some(item => item?.city_id || item?.state_id)
  }

  const splitArrayLocation = list => {
    if (!Array.isArray(list)) return ''
    return list
      .filter(item => item?.city_id || item?.state_id)
      .map(item => (item?.city_name ? item?.city_name : item?.state_name))
      .join(', ')
  }

  const [listData, setListData] = useState([])

  const hasFieldPending = (fieldName) => {
    return pendingData?.studentPersonalProfile?._pendingFields && 
           pendingData.studentPersonalProfile._pendingFields[fieldName] !== undefined
  }

  useEffect(() => {
    const approvedProfile = studentDetails?.studentPersonalProfile || {}
    const pendingProfile = pendingData?.studentPersonalProfile || {}
    const approvedAdmin = studentDetails?.admin || {}
    const pendingAdmin = pendingData?.admin || {}
    
    const hasPendingRequest = pendingProfile?._requestId || pendingAdmin?._requestId
    
    const hasFieldChanged = (approvedValue, pendingValue, fieldKey) => {
      if (!hasPendingRequest) return false
      
      const pendingFieldData = pendingProfile?.[fieldKey]
      if (pendingFieldData && typeof pendingFieldData === 'object' && pendingFieldData.value !== undefined) {
        return pendingFieldData._status === 'PENDING' && pendingFieldData.value !== approvedValue
      }
      
      if (pendingValue === null) return false
      
      if (Array.isArray(approvedValue) && Array.isArray(pendingValue)) {
        return JSON.stringify(approvedValue) !== JSON.stringify(pendingValue)
      }
      
      return pendingValue !== undefined && pendingValue !== approvedValue
    }
    
    const mergeProfileData = () => {
      if (!hasPendingRequest || Object.keys(pendingProfile).length <= 1) {
        return approvedProfile
      }
      
      const merged = { ...approvedProfile }
      
      Object.keys(pendingProfile).forEach(key => {
        if (key === '_requestId') return
        
        const pendingValue = pendingProfile[key]
        if (pendingValue && typeof pendingValue === 'object' && pendingValue.value !== undefined) {
          if (pendingValue._status === 'PENDING') {
            merged[key] = pendingValue.value
          }
        } else if (pendingValue !== undefined && pendingValue !== null) {
          merged[key] = pendingValue
        }
      })
      
      return merged
    }
    
    const displayProfile = mergeProfileData()
    const displayAdmin = hasPendingRequest && Object.keys(pendingAdmin).length > 1 ? pendingAdmin : approvedAdmin
    
    let otherData = [
      ...(displayProfile?.isAnywhere || hasValidLocation(displayProfile?.preferredJobLocation)
        ? [{
            id: 1,
            name: 'Preferred Location',
            value: displayProfile?.isAnywhere
              ? 'Anywhere'
              : splitArrayLocation(displayProfile?.preferredJobLocation),
            isPending: hasFieldChanged(
              approvedProfile?.isAnywhere ? 'Anywhere' : splitArrayLocation(approvedProfile?.preferredJobLocation),
              displayProfile?.isAnywhere ? 'Anywhere' : splitArrayLocation(displayProfile?.preferredJobLocation),
              'preferredJobLocation'
            ),
          }]
        : []),
      {
        id: 2,
        name: 'Gender',
        value: displayProfile?.gender?.replace(/_/g, ' ') || '',
        isPending: hasFieldChanged(
          approvedProfile?.gender,
          displayProfile?.gender,
          'gender'
        ),
      },

      {
        id: 4,
        name: 'Date of Birth',
        value: (() => {
          const dob = displayProfile?.dob || displayAdmin?.dob
          return dob ? moment(dob).format('DD/MM/YYYY') : ''
        })(),
        isPending: hasFieldChanged(
          approvedProfile?.dob || approvedAdmin?.dob,
          displayProfile?.dob || displayAdmin?.dob,
          'dob'
        ),
      },
      {
        id: 5,
        name: 'Secondary Email ID',
        value: displayProfile?.secondaryEmail,
        isPending: hasFieldChanged(approvedProfile?.secondaryEmail, displayProfile?.secondaryEmail, 'secondaryEmail'),
      },
      // {
      //   id: 6,
      //   name: 'Caste',
      //   value: studentDetails?.studentPersonalProfile?.caste,
      // },
      // {
      //   id: 7,
      //   name: 'Religion',
      //   value: studentDetails?.studentPersonalProfile?.religion,
      // },
      {
        id: 8,
        name: 'Language',
        value: splitArray(displayProfile?.languages),
        isPending: hasFieldChanged(
          splitArray(approvedProfile?.languages),
          splitArray(displayProfile?.languages),
          'languages'
        ),
      },
      // {
      //   id: 9,
      //   name: 'Hobbies',
      //   value: splitArray(studentDetails?.studentPersonalProfile?.hobbies),
      // },
      {
        id: 10,
        name: 'Current Address',
        value: (
          <>
            {displayProfile?.corrAddrLine1}
            {displayProfile?.corrAddrLine1 && ','}
            {displayProfile?.corrAddrLine2}
            {displayProfile?.corrAddrLine2 && ','}
            {displayProfile?.corrCity}
            {displayProfile?.corrCity && ','}
            {displayProfile?.corrState}
            {displayProfile?.corrState && ','}
            {displayProfile?.corrCountry}
            {displayProfile?.corrCountry && ','}
            {displayProfile?.corrPostCode}
          </>
        ),
        isPending: ['corrAddrLine1', 'corrAddrLine2', 'corrCity', 'corrState', 'corrCountry', 'corrPostCode'].some(field => 
          hasFieldChanged(approvedProfile?.[field], displayProfile?.[field], field)
        ),
      },
      {
        id: 11,
        name: 'Permanent Address',
        value: (
          <>
            {displayProfile?.permAddrLine1}
            {displayProfile?.permAddrLine1 && ','}
            {displayProfile?.permAddrLine2}
            {displayProfile?.permAddrLine2 && ','}
            {displayProfile?.permCity}
            {displayProfile?.permCity && ','}
            {displayProfile?.permState}
            {displayProfile?.permState && ','}
            {displayProfile?.permCountry}
            {displayProfile?.permCountry && ','}
            {displayProfile?.permPostCode}
          </>
        ),
        isPending: ['permAddrLine1', 'permAddrLine2', 'permCity', 'permState', 'permCountry', 'permPostCode'].some(field => 
          hasFieldChanged(approvedProfile?.[field], displayProfile?.[field], field)
        ),
      },
    ]
    setListData(otherData)
  }, [studentDetails, pendingData])
const cleanText = html => html?.replace(/<[^>]*>/g, '').trim()

  return (
    <SubFLex column gap="20px">
      {listData?.map((item, index) => (
        <Row style={{ width: '100%' }} key={index + 'list'}>
          <Col span={10}>
            <SecondGreyText color={COLORS.GREY_T_60}>
              {item?.name}
            </SecondGreyText>
          </Col>
          <Col span={2}>
            {' '}
            <SecondGreyText>-</SecondGreyText>{' '}
          </Col>
          <Col span={12}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SecondGreyText>{item?.value}</SecondGreyText>
              {item?.isPending && <PendingIcon />}
            </div>
          </Col>
        </Row>
      ))}

      {/* Extracurricular Activities */}
      {cleanText(studentDetails?.studentPersonalProfile?.extraCurriculars || pendingData?.studentPersonalProfile?.extraCurriculars) && (
        <>
          <MainHeading marginBottom="5px" style={{ display: 'flex', alignItems: 'center' }}>
            {' '}
            Extracurricular Activities
            {hasFieldPending('extraCurriculars') && <PendingIcon />}
          </MainHeading>
          <StyledP>
            <EditorContent
              dangerouslySetInnerHTML={{
                __html:
                  (pendingData?.studentPersonalProfile?._requestId && pendingData?.studentPersonalProfile?.extraCurriculars)
                    ? pendingData?.studentPersonalProfile?.extraCurriculars
                    : studentDetails?.studentPersonalProfile?.extraCurriculars || '',
              }}
            />
          </StyledP>
        </>
      )}
    </SubFLex>
  )
}

export default AdditionalInfo
